import { useState } from 'react';
import { X, Plus, Minus, RotateCcw } from 'lucide-react';

export const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    contrast: 'normal',
    highlightLinks: false,
    bigCursor: false,
    readingGuide: false
  });

  const adjustFontSize = (increment) => {
    setSettings(prev => ({
      ...prev,
      fontSize: Math.min(Math.max(prev.fontSize + increment, 80), 150)
    }));
    document.documentElement.style.fontSize = `${(settings.fontSize + increment)}%`;
  };

  const toggleContrast = () => {
    const modes = ['normal', 'high', 'inverted'];
    const currentIndex = modes.indexOf(settings.contrast);
    const nextMode = modes[(currentIndex + 1) % modes.length];
    setSettings(prev => ({ ...prev, contrast: nextMode }));
    
    document.body.classList.remove('high-contrast', 'inverted-contrast');
    if (nextMode === 'high') {
      document.body.classList.add('high-contrast');
    } else if (nextMode === 'inverted') {
      document.body.classList.add('inverted-contrast');
    }
  };

  const toggleHighlightLinks = () => {
    setSettings(prev => ({ ...prev, highlightLinks: !prev.highlightLinks }));
    document.body.classList.toggle('highlight-links');
  };

  const toggleBigCursor = () => {
    setSettings(prev => ({ ...prev, bigCursor: !prev.bigCursor }));
    document.body.classList.toggle('big-cursor');
  };

  const resetAll = () => {
    setSettings({
      fontSize: 100,
      contrast: 'normal',
      highlightLinks: false,
      bigCursor: false,
      readingGuide: false
    });
    document.documentElement.style.fontSize = '100%';
    document.body.classList.remove('high-contrast', 'inverted-contrast', 'highlight-links', 'big-cursor');
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 w-9 h-9 sm:w-12 sm:h-12 opacity-80 sm:opacity-100 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex items-center justify-center z-40 shadow-lg transition-colors"
        aria-label="Accessibility Options"
        data-testid="accessibility-widget-btn"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="currentColor" 
          className="w-6 h-6"
        >
          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9H15V22H13V16H11V22H9V9H3V7H21V9Z"/>
        </svg>
      </button>

      {/* Widget Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-20 left-6 w-72 bg-white rounded-xl shadow-2xl z-50 overflow-hidden"
          data-testid="accessibility-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-emerald-600 text-white">
            <div className="flex items-center gap-2">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="currentColor" 
                className="w-5 h-5"
              >
                <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9H15V22H13V16H11V22H9V9H3V7H21V9Z"/>
              </svg>
              <span className="font-semibold">Accessibility</span>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close accessibility panel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Controls */}
          <div className="p-4 space-y-4">
            {/* Font Size */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Text Size: {settings.fontSize}%
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => adjustFontSize(-10)}
                  className="flex-1 py-2 px-3 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center gap-1 text-sm font-medium transition-colors"
                  aria-label="Decrease text size"
                >
                  <Minus className="w-4 h-4" /> Smaller
                </button>
                <button
                  onClick={() => adjustFontSize(10)}
                  className="flex-1 py-2 px-3 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center gap-1 text-sm font-medium transition-colors"
                  aria-label="Increase text size"
                >
                  <Plus className="w-4 h-4" /> Larger
                </button>
              </div>
            </div>

            {/* Contrast */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Contrast Mode
              </label>
              <button
                onClick={toggleContrast}
                className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  settings.contrast !== 'normal' 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {settings.contrast === 'normal' && 'Normal Contrast'}
                {settings.contrast === 'high' && 'High Contrast'}
                {settings.contrast === 'inverted' && 'Inverted Colors'}
              </button>
            </div>

            {/* Highlight Links */}
            <button
              onClick={toggleHighlightLinks}
              className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                settings.highlightLinks 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              {settings.highlightLinks ? '✓ Links Highlighted' : 'Highlight Links'}
            </button>

            {/* Big Cursor */}
            <button
              onClick={toggleBigCursor}
              className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                settings.bigCursor 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              {settings.bigCursor ? '✓ Large Cursor' : 'Large Cursor'}
            </button>

            {/* Reset */}
            <button
              onClick={resetAll}
              className="w-full py-2 px-3 bg-gray-800 hover:bg-gray-900 text-white rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Reset All
            </button>

            {/* Accessibility Statement Link */}
            <a
              href="/accessibility"
              className="block text-center text-sm text-emerald-600 hover:underline font-medium pt-2"
            >
              View Accessibility Statement
            </a>
          </div>
        </div>
      )}

      {/* Global Styles for Accessibility */}
      <style>{`
        .high-contrast {
          filter: contrast(1.5);
        }
        .inverted-contrast {
          filter: invert(1) hue-rotate(180deg);
        }
        .inverted-contrast img,
        .inverted-contrast video {
          filter: invert(1) hue-rotate(180deg);
        }
        .highlight-links a {
          background-color: yellow !important;
          color: black !important;
          text-decoration: underline !important;
        }
        .big-cursor,
        .big-cursor * {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24'%3E%3Cpath fill='%23000' d='M5 3l14 9-8 2-4 8z'/%3E%3C/svg%3E") 0 0, auto !important;
        }
      `}</style>
    </>
  );
};

export default AccessibilityWidget;
